import type { Building } from "building-types/building/buildingTypes";
import type { BuildingAnalyticEntity, BuildingAnalyticEntityStyle } from "building-types/buildingAnalyticEntityTypes";
import type { BuildingGeomParent } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import {
  CLIP_EXTERIOR_FINISH,
  EXTERIOR,
  INTERIOR,
  TAG_POSITION_BACK,
  TAG_POSITION_FRONT,
  WINDOW,
  WINDOW_TRIM,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { OpeningTrim } from "building-types/casing/openingTrimTypes";
import type { BuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { WindowUnit, GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { RWTable, Table } from "common/data/table";
import { percentNew, worldInchesNew } from "common/utils/nominalNumbers";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { faceNew } from "geom/brep";
import { polyLine2OnPlane } from "geom/plane";
import { windowTrimClipsExteriorFinish } from "../elements/window/windowQuery";
import { addAnalyticFace, addPolyLine3Edges } from "../utils/generateUtil";
import makeBuildingSelector from "../utils/makeBuildingSelector";
import memo from "../utils/memo";

const openingSurfaceStyle: BuildingAnalyticEntityStyle = {
  color: 0x00ffff,
  opacity: percentNew(0.1),
  lineWidth3d: worldInchesNew(0.1),
};

const trimLineStyle: BuildingAnalyticEntityStyle = {
  color: 0xddaa22,
  opacity: percentNew(0.5),
  lineWidth3d: worldInchesNew(4),
};

const trimSurfaceStyle: BuildingAnalyticEntityStyle = {
  color: 0xddaa22,
  edgeColor: 0xddaa22,
  opacity: percentNew(0.25),
};

function addExteriorTrimAnalyticEntity(
  entities: RWTable<BuildingAnalyticEntity>,
  window: WindowUnit,
  parents: DocEntityHandle[],
  trim: OpeningTrim,
  sideTag: BuildingGeomTag,
) {
  const opening = window.windowPlacement.opening;
  if (!opening) {
    return;
  }

  const windowTag: BuildingGeomParent = {
    type: "PARENT",
    parentType: "WINDOW",
    parentId: window.windowPlacement.id,
  };
  const trimTag: BuildingGeomParent = {
    type: "PARENT",
    parentType: "EXTERIOR_OPENING_TRIM",
    parentId: trim.id,
  };
  const trimTags: BuildingGeomTag[] = [WINDOW_TRIM, windowTag, sideTag, trimTag];
  if (windowTrimClipsExteriorFinish(window.windowPlacement, trim)) {
    trimTags.push(CLIP_EXTERIOR_FINISH);
  }

  if (trim.rail) {
    const rail3 = polyLine2OnPlane(opening.backPlane, trim.rail);
    addPolyLine3Edges(
      "ANALYTIC_WINDOW_TRIM",
      entities,
      "Window " + window.windowPlacement.descriptor.reportedName + " " + trim.type,
      parents,
      rail3,
      trimTags,
      trimLineStyle,
    );
  }

  if (trim.elevationProfile && trim.elevationProfile.length > 0) {
    const face = faceNew(trim.elevationProfile, opening.backPlane);
    addAnalyticFace(
      "ANALYTIC_WINDOW_TRIM",
      entities,
      "Window " + window.windowPlacement.descriptor.reportedName + " " + trim.type + " surface",
      parents,
      face,
      undefined,
      trimTags,
      trimSurfaceStyle,
      trim.elevationProfileEdgeTypes,
    );
  }
}

type Access = "interior" | "exterior";

function windowAccess(window: GeneratedWindowPlacement): Access {
  const { frontRoom, backRoom } = window;

  // If either room is not an interior room enclosure, it's exterior.
  if (!frontRoom || !isInteriorRoomEnclosure[frontRoom.attributes.roomEnclosure]) {
    return "exterior";
  }
  if (!backRoom || !isInteriorRoomEnclosure[backRoom.attributes.roomEnclosure]) {
    return "exterior";
  }

  return "interior";
}

function windowOpeningSurfaceTags(access: Access): BuildingGeomTag[] {
  return access === "interior" ? [WINDOW, INTERIOR] : [WINDOW, EXTERIOR];
}

export function addWindowAnalyticEntities(
  building: ReturnType<typeof select>,
  handle: BuildingEntityHandle,
): Table<BuildingAnalyticEntity> {
  const entities: RWTable<BuildingAnalyticEntity> = {};

  const window = building.windowUnits[handle.id];
  if (window?.entityType !== "WINDOW" || !window?.windowPlacement?.opening) {
    return entities;
  }

  const parents: DocEntityHandle[] = [
    {
      type: "GENERATED_ENTITY",
      entity: handle,
    },
  ];

  if (window.docEntityHandle) {
    parents.push(window.docEntityHandle);
  }

  if (window.windowPlacement.handle) {
    parents.push(window.windowPlacement.handle);
  }

  const opening = window.windowPlacement.opening;
  const access = windowAccess(window.windowPlacement);
  const openingSurfaceTags = windowOpeningSurfaceTags(access);

  if (access === "exterior") {
    for (const trim of opening?.back?.trim) {
      addExteriorTrimAnalyticEntity(entities, window, parents, trim, TAG_POSITION_BACK);
    }
  }

  const frontOpening = opening.front;
  if (frontOpening) {
    const face = faceNew([frontOpening.opening2], opening.frontPlane);
    addAnalyticFace(
      "ANALYTIC_WINDOW_TRIM",
      entities,
      "Window front opening",
      parents,
      face,
      undefined,
      [TAG_POSITION_FRONT, ...openingSurfaceTags],
      {
        ...openingSurfaceStyle,
      },
      frontOpening.opening2EdgeTypes ? [frontOpening.opening2EdgeTypes] : undefined,
    );
  }
  const backOpening = opening.back;
  if (backOpening) {
    const face = faceNew([backOpening.opening2], opening.backPlane);
    addAnalyticFace(
      "ANALYTIC_WINDOW_TRIM",
      entities,
      "Window back opening",
      parents,
      face,
      undefined,
      [TAG_POSITION_BACK, ...openingSurfaceTags],
      {
        ...openingSurfaceStyle,
      },
      backOpening.opening2EdgeTypes ? [backOpening.opening2EdgeTypes] : undefined,
    );
  }

  return entities;
}

const select = makeBuildingSelector(["windowUnits"]);

export function generate(building: ReturnType<typeof select>): Pick<Building, "analyticWindowTrimEntities"> {
  const analyticWindowTrimEntities: RWTable<BuildingAnalyticEntity> = {};
  for (const id of Object.keys(building.windowUnits)) {
    Object.assign(analyticWindowTrimEntities, addWindowAnalyticEntities(building, { entityType: "WINDOW", id }));
  }
  return { analyticWindowTrimEntities };
}

export default memo(select, generate);
